import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { leaseAPI, unitAPI, propertyAPI } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  FileText,
  Calendar,
  DollarSign,
  Home,
  MapPin,
  User,
  Download,
  File
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const TenantLease = () => {
  const { t } = useI18n();
  const [lease, setLease] = useState(null);
  const [unit, setUnit] = useState(null);
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaseData();
  }, []);

  const loadLeaseData = async () => {
    try {
      const leasesRes = await leaseAPI.getAll();
      const activeLease = leasesRes.data.find(l => l.status === 'ACTIVE');
      
      if (activeLease) {
        setLease(activeLease);
        setDocuments(activeLease.documents || []);
        
        const [unitsRes, propertiesRes] = await Promise.all([
          unitAPI.getAll(),
          propertyAPI.getAll()
        ]);
        
        const leaseUnit = unitsRes.data.find(u => u.id === activeLease.unit_id);
        setUnit(leaseUnit);
        
        if (leaseUnit) {
          const leaseProperty = propertiesRes.data.find(p => p.id === leaseUnit.property_id);
          setProperty(leaseProperty);
        }
      }
    } catch (error) {
      toast.error('Failed to load lease information');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(doc.url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.lease')}>
        <div className="space-y-6">
          <div className="h-48 bg-card animate-pulse" />
          <div className="h-64 bg-card animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (!lease) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.lease')}>
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-serif mb-2">No Active Lease</h3>
            <p className="text-muted-foreground">
              You don't have an active lease agreement. Contact your landlord for assistance.
            </p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.lease')}>
      <div className="space-y-6" data-testid="tenant-lease-page">
        {/* Property Info */}
        {property && (
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div 
                  className="w-24 h-24 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${property.image_url || 'https://images.unsplash.com/photo-1637747022660-12ce5ce4e420?w=200'})`
                  }}
                />
                <div>
                  <h3 className="font-serif text-2xl mb-2">{property.name}</h3>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{property.address}, {property.city}, {property.state} {property.zip_code}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unit Details */}
        {unit && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Home className="w-5 h-5 text-gold" />
                Unit Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Unit</p>
                  <p className="text-xl font-semibold">{unit.unit_number}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Bedrooms</p>
                  <p className="text-xl font-semibold">{unit.bedrooms === 0 ? 'Studio' : unit.bedrooms}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Bathrooms</p>
                  <p className="text-xl font-semibold">{unit.bathrooms}</p>
                </div>
                {unit.square_feet && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Size</p>
                    <p className="text-xl font-semibold">{unit.square_feet} sqft</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lease Details */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif flex items-center gap-2">
              <FileText className="w-5 h-5 text-gold" />
              Lease Agreement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Lease Period</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gold" />
                    <span className="text-lg">{lease.start_date} - {lease.end_date}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                  <span className="px-3 py-1 text-sm uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {lease.status}
                  </span>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Monthly Rent</p>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gold" />
                    <span className="text-2xl font-semibold text-gold">
                      ${lease.monthly_rent?.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Security Deposit</p>
                  <span className="text-lg">${lease.deposit_amount?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {lease.terms && (
              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Terms & Conditions</p>
                <p className="text-muted-foreground whitespace-pre-line">{lease.terms}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lease Documents */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-serif flex items-center gap-2">
              <File className="w-5 h-5 text-gold" />
              Lease Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No documents have been uploaded for this lease yet.
              </p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between bg-background/50 p-4 hover:bg-background/80 transition-colors"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gold/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDownload(doc)}
                      className="bg-gold text-white hover:bg-gold-light"
                      data-testid={`download-btn-${doc.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Info */}
        <Card className="bg-gold/10 border-gold/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <DollarSign className="w-8 h-8 text-gold" />
              <div>
                <h4 className="font-medium">Rent Payment Information</h4>
                <p className="text-sm text-muted-foreground">
                  Rent is due on the 1st of each month. A reminder will be sent on the 3rd if unpaid.
                  A 5% late fee applies after the 5th.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TenantLease;
